import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Cookie from 'cookie-universal';
import { baseURL, LOGIN, REGISTER } from '../../../API/Api';
import Loading from '../../../components/Loading/Loding';
import { useLanguage } from '../../../Context/LanguageContext';
import './Auth.css';

const cookieJar = Cookie();

/* ------------------------------------------------------------------ */
/* Small inline icons                                                   */
/* ------------------------------------------------------------------ */
const UserIcon = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="12" cy="8" r="3.4" />
        <path d="M5 20c0-3.9 3.1-6.5 7-6.5s7 2.6 7 6.5" strokeLinecap="round" />
    </svg>
);

const MailIcon = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="3.5" y="5.5" width="17" height="13" rx="2.2" />
        <path d="M4.5 7 12 12.5 19.5 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const LockIcon = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="5" y="10.5" width="14" height="9.5" rx="2" />
        <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" strokeLinecap="round" />
    </svg>
);

const GoogleIcon = () => (
    <svg viewBox="0 0 48 48" width="20" height="20">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.6 29.4 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z" />
        <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.6 15.9 18.9 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6 29.6 4 24 4c-7.5 0-14 4.2-17.7 10.7z" />
        <path fill="#4CAF50" d="M24 44c5.5 0 10.4-1.9 14.2-5.1l-6.6-5.5C29.6 35.3 27 36 24 36c-5.3 0-9.8-3.4-11.4-8.1l-6.6 5.1C9.9 39.7 16.4 44 24 44z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.9 2.6-2.6 4.8-4.8 6.4l6.6 5.5C40.9 36.9 44 31 44 24c0-1.2-.1-2.4-.4-3.5z" />
    </svg>
);

/* ------------------------------------------------------------------ */
/* Floating-label field                                                 */
/* ------------------------------------------------------------------ */
function Field ( { id, icon, label, inputRef, ...inputProps } )
{
    return (
        <div className="field">
            <span className="field-icon">{ icon }</span>
            <input id={ id } ref={ inputRef } placeholder=" " { ...inputProps } />
            <label htmlFor={ id }>{ label }</label>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Helper — استخرج الـ role ID بغض النظر عن شكل الـ API response      */
/* الـ role قد يكون: رقم، string، أو object { id, name, type }        */
/* ------------------------------------------------------------------ */
function extractRoleId ( user )
{
    const role = user?.role;
    if ( !role ) return null;
    // object مع id  ← { id: 3, name: "Authenticated", type: "authenticated" }
    if ( typeof role === 'object' && role.id !== undefined )
    {
        return role.id?.toString();
    }
    // string أو number مباشر
    return role?.toString();
}

export default function Auth ()
{
    const { t, dir } = useLanguage();
    const [ mode, setMode ] = useState( 'login' ); // 'login' | 'register'
    const [ formData, setFormData ] = useState( { name: '', email: '', password: '' } );
    const [ loading, setLoading ] = useState( false );
    const [ error, setError ] = useState( '' );
    const navigate = useNavigate();
    const location = useLocation();
    const nameRef = useRef( null );
    const isLogin = mode === 'login';

    useEffect( () =>
    {
        setError( '' );
        if ( !isLogin )
        {
            const timer = setTimeout( () => nameRef.current?.focus(), 260 );
            return () => clearTimeout( timer );
        }
    }, [ mode, isLogin ] );

    const handleChange = ( e ) =>
    {
        const { name, value } = e.target;
        setFormData( ( prev ) => ( { ...prev, [ name ]: value } ) );
    };

    const switchMode = ( nextMode ) =>
    {
        if ( nextMode === mode ) return;
        setMode( nextMode );
        setFormData( ( prev ) => ( { name: '', email: prev.email, password: '' } ) );
    };

    const handleSubmit = async ( e ) =>
    {
        e.preventDefault();
        setError( '' );
        setLoading( true );

        const endpoint = isLogin ? LOGIN : REGISTER;
        const payload = isLogin
            ? { email: formData.email, password: formData.password }
            : formData;

        const errorsByStatus = isLogin
            ? { 422: t( 'errors.invalidCredentials' ) }
            : { 422: t( 'errors.emailTaken' ) };

        try
        {
            const { data } = await axios.post( `${ baseURL }/${ endpoint }`, payload );

            // ── حفظ التوكن ──────────────────────────────────────────
            cookieJar.set( 'ecommerce', data.token );

            if ( isLogin )
            {
                // ── إذا جاي من صفحة ثانية، رجّعه هناك ─────────────
                const returnTo = location.state?.from;
                if ( returnTo )
                {
                    navigate( returnTo, { replace: true } );
                    return;
                }

                // ── استخرج الـ role ID بشكل آمن ─────────────────────
                const roleId = extractRoleId( data.user );

                // أرقام الـ roles في الداشبورد فقط
                const dashboardRoles = {
                    '1995': 'users',
                    '1999': 'categories',
                    '1996': 'writer',
                };

                const dashPage = dashboardRoles[ roleId ];

                if ( dashPage )
                {
                    // مستخدم داشبورد (admin / editor / writer)
                    navigate( `/dashboard/${ dashPage }`, { replace: true } );
                } else
                {
                    // مستخدم عادي (Authenticated) ← يروح للموقع
                    navigate( '/', { replace: true } );
                }

            } else
            {
                // ── تسجيل حساب جديد ← دائماً يروح للموقع ──────────
                // المستخدم الجديد role حقه "Authenticated" وليس داشبورد
                navigate( '/', { replace: true } );
            }

        } catch ( err )
        {
            const status = err.response?.status;
            setError(
                err.response
                    ? ( errorsByStatus[ status ] || t( 'errors.serverError' ) )
                    : t( 'errors.networkError' )
            );
        } finally
        {
            setLoading( false );
        }
    };

    return (
        <div className="auth-page" dir={ dir }>
            { loading && <Loading /> }

            <div className="auth-card">
                <aside className="auth-brand">
                    <div className="brand-glow" aria-hidden="true" />
                    <div className="price-tag" aria-hidden="true">
                        <span>{ t( 'auth.discountTag' ) }</span>
                    </div>

                    <div className="brand-content">
                        <span className="brand-mark">{ t( 'auth.brandMark' ) }</span>
                        <h1>{ isLogin ? t( 'auth.welcomeBackTitle' ) : t( 'auth.joinStoreTitle' ) }</h1>
                        <p>{ isLogin ? t( 'auth.welcomeBackDesc' ) : t( 'auth.joinStoreDesc' ) }</p>
                    </div>

                    <ul className="brand-perks">
                        <li>{ t( 'auth.perkDelivery' ) }</li>
                        <li>{ t( 'auth.perkOffers' ) }</li>
                        <li>{ t( 'auth.perkReturns' ) }</li>
                    </ul>
                </aside>

                <div className="auth-tear" aria-hidden="true" />

                <section className="auth-form-panel">
                    <div className="mode-switch" role="tablist">
                        <button
                            type="button" role="tab"
                            aria-selected={ isLogin }
                            className={ isLogin ? 'active' : '' }
                            onClick={ () => switchMode( 'login' ) }
                        >
                            { t( 'auth.loginTab' ) }
                        </button>
                        <button
                            type="button" role="tab"
                            aria-selected={ !isLogin }
                            className={ !isLogin ? 'active' : '' }
                            onClick={ () => switchMode( 'register' ) }
                        >
                            { t( 'auth.registerTab' ) }
                        </button>
                        <span className={ `indicator ${ isLogin ? 'at-start' : 'at-end' }` } />
                    </div>

                    <form className="auth-form" onSubmit={ handleSubmit } noValidate>
                        { !isLogin && (
                            <Field
                                id="name" icon={ <UserIcon /> } label={ t( 'auth.nameLabel' ) }
                                name="name" type="text" value={ formData.name }
                                onChange={ handleChange } inputRef={ nameRef } required
                            />
                        ) }

                        <Field
                            id="email" icon={ <MailIcon /> } label={ t( 'auth.emailLabel' ) }
                            name="email" type="email" value={ formData.email }
                            onChange={ handleChange } required
                        />

                        <Field
                            id="password" icon={ <LockIcon /> } label={ t( 'auth.passwordLabel' ) }
                            name="password" type="password" value={ formData.password }
                            onChange={ handleChange } required minLength={ 6 }
                        />

                        { error && <p className="auth-error">{ error }</p> }

                        <button type="submit" className="submit-btn" disabled={ loading }>
                            { isLogin ? t( 'auth.loginButton' ) : t( 'auth.registerButton' ) }
                        </button>

                        <div className="divider"><span>{ t( 'common.or' ) }</span></div>

                        <a className="google-btn" href="http://127.0.0.1:8000/login-google">
                            <GoogleIcon />
                            <span>{ t( 'auth.continueWithGoogle' ) }</span>
                        </a>

                        <p className="switch-line">
                            { isLogin ? t( 'auth.noAccount' ) : t( 'auth.haveAccount' ) }
                            <button
                                type="button"
                                onClick={ () => switchMode( isLogin ? 'register' : 'login' ) }
                            >
                                { isLogin ? t( 'auth.createOne' ) : t( 'auth.loginNow' ) }
                            </button>
                        </p>
                    </form>
                </section>
            </div>
        </div>
    );
}
